/**
 * tokens.ts — the one rule that decides whether a signing link is still a link.
 *
 * A token row is a LEDGER, not a switch: it records an expiry, a revocation, a consumption
 * and a use count, and "is there a live link out there?" is the conjunction of all four.
 * Reading only `revoked_at` shows a stale link as live; reading only `expires_at` shows a
 * revoked one as live. Both mistakes end the same way — an admin waiting on a signature
 * through a link that cannot be opened.
 *
 * The raw token is not part of this. It exists once, in the response that minted it, and
 * never again; everything here is about the half the database keeps.
 */

import type { AgreementTokenRow } from './api';

/**
 * The server's own ceiling, mirrored: both `agreement_token_lookup` and
 * `agreement_token_sign` refuse at `use_count >= 20`. A link that has been opened twenty
 * times is exhausted whatever its expiry says, so a surface that ignores this offers a
 * "copy the live link" button for a link the next click will refuse.
 */
const MAX_USES = 20;

export function isSigningLinkLive(
    token: Pick<AgreementTokenRow, 'expires_at' | 'consumed_at' | 'revoked_at' | 'use_count'> | null | undefined,
    now: Date = new Date(),
): boolean {
    if (!token) return false;
    if (token.revoked_at) return false;
    if (token.consumed_at) return false;
    if ((token.use_count ?? 0) >= MAX_USES) return false;
    return new Date(token.expires_at).getTime() > now.getTime();
}

/**
 * The live token for one document and party, newest first.
 *
 * ISSUING REVOKES WHAT IT REPLACES, so at most one row can survive this filter — but the
 * table keeps every token that was ever minted, and picking the first row that merely
 * matches the document would hand back a revoked one as often as not.
 */
export function liveTokenFor(
    tokens: AgreementTokenRow[] | null | undefined,
    documentId: number | null | undefined,
    party: string,
    now: Date = new Date(),
): AgreementTokenRow | null {
    if (!tokens || documentId == null) return null;
    return (
        tokens.find(
            (token) =>
                token.document_id === documentId &&
                token.party === party &&
                isSigningLinkLive(token, now),
        ) ?? null
    );
}
